/**
 * Helpers for the DRHP & IPO Prospectus Tracker (/drhp-filings).
 *
 * One row per company in GET /api/core/ipos/drhp-tracker, which carries the
 * board, the regulatory status (under_review, or approved once the RHP is
 * filed), the DRHP/RHP filing timestamps and the backend's extraction
 * pipeline status. Rows are joined with:
 * - extracted DRHP details (GET /api/core/ipos/{id}) for sector, lead managers
 *   and issue structure, for filings whose analysis is approved
 * - the GMP board (GET /api/market/gmp) and NSE schedules (GET
 *   /api/market/ipos/upcoming, /api/market/ipos/sme/upcoming) for bidding
 *   dates, price band, issue size and the stage after the RHP
 */
import { deriveBoard, toIsoDate } from './ipoCatalogAdapters';
import { toLocalIsoDate } from './gmpBoard';
import { isSameCompany, matchKey } from './smeIpos';
import { todayIso } from './ipoCalendar';

export const DRHP_STATUS = {
  bidding_open: { label: 'Bidding Open', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', rank: 0 },
  bidding_soon: { label: 'Bidding Soon', badge: 'bg-amber-50 text-amber-700 border-amber-200', rank: 1 },
  rhp_filed: { label: 'Approved · RHP Filed', badge: 'bg-teal-50 text-[#006666] border-teal-200', rank: 2 },
  closed: { label: 'Bidding Closed', badge: 'bg-slate-100 text-slate-600 border-slate-200', rank: 3 },
  allotted: { label: 'Allotted', badge: 'bg-orange-50 text-orange-700 border-orange-200', rank: 3 },
  listed: { label: 'Listed', badge: 'bg-violet-50 text-violet-700 border-violet-200', rank: 3 },
  under_review: { label: 'Under SEBI Review', badge: 'bg-sky-50 text-sky-700 border-sky-200', rank: 4 },
  drhp_filed: { label: 'DRHP Filed', badge: 'bg-slate-50 text-slate-600 border-slate-200', rank: 4 },
};

// Tracker status -> row status, when no market feed places the issue further along
const TRACKER_STATUS = { approved: 'rhp_filed', under_review: 'under_review' };

export const DOC_TYPES = {
  DRHP: 'bg-sky-50 text-sky-700 border-sky-200',
  RHP: 'bg-[#008080] text-white border-[#008080]',
  Prospectus: 'bg-slate-800 text-white border-slate-800',
};

export const DRHP_TABS = [
  { key: 'all', label: 'All Prospectuses', test: () => true },
  { key: 'mainboard', label: 'Mainboard DRHP', test: (row) => row.board === 'mainboard' },
  { key: 'sme', label: 'SME IPO Prospectus', test: (row) => row.board === 'sme' },
];

export const drhpTabByKey = (key) => DRHP_TABS.find((tab) => tab.key === key) || DRHP_TABS[0];

export const EXCHANGE_OPTIONS = ['NSE', 'BSE', 'NSE Emerge', 'BSE SME'];

// "23-Sep-2026", "Sep 22, 2026", "September 30, 2025" or ISO -> "2026-09-23"
export const parseLooseDate = (value) => {
  if (!value) return null;
  const iso = toIsoDate(value);
  if (iso) return iso;
  const time = Date.parse(value);
  return Number.isFinite(time) ? toLocalIsoDate(new Date(time)) : null;
};

const exchangeList = (text, board) => {
  const found = [];
  if (/nse|emerge/i.test(text || '')) found.push(board === 'sme' ? 'NSE Emerge' : 'NSE');
  if (/bse/i.test(text || '')) found.push(board === 'sme' ? 'BSE SME' : 'BSE');
  return found;
};

const firstText = (...values) => values.find((value) => typeof value === 'string' && value.trim()) || null;

const statusFromMarket = (schedule, gmp) => {
  if (schedule) return schedule.phase === 'open' ? 'bidding_open' : 'bidding_soon';
  if (!gmp) return null;
  if (gmp.status === 'open' || gmp.status === 'closing_today') return 'bidding_open';
  if (gmp.status === 'upcoming' && gmp.openOn) return 'bidding_soon';
  if (['closed', 'allotted', 'listed'].includes(gmp.status)) return gmp.status;
  return null;
};

const docTypeFor = (status) => {
  if (status === 'bidding_open' || status === 'bidding_soon' || status === 'rhp_filed') return 'RHP';
  if (status === 'closed' || status === 'allotted' || status === 'listed') return 'Prospectus';
  return 'DRHP';
};

// ₹ crore per unit named in an extracted amount ("₹ 450.00 crore", "7,270 million")
const UNIT_TO_CRORE = [
  [/\b(?:crores?|cr)\b/i, 1],
  [/\b(?:millions?|mn)\b/i, 0.1],
  [/\b(?:lakhs?|lacs?)\b/i, 0.01],
];

// Fresh issue in ₹ crore, or the share count when that's all the DRHP states.
// Extracted amounts usually come without a unit, and DRHPs report in lakhs,
// millions or crores, so a bare number is never read as crores.
const issueFromDetail = (detail) => {
  const structure = detail?.raw?.json_data?.ipo_structure || {};
  const amountText = String(structure.fresh_issue_amount || '');
  const amount = Number(amountText.replace(/[^\d.]/g, ''));
  const [, perUnit] = UNIT_TO_CRORE.find(([pattern]) => pattern.test(amountText)) || [];
  if (amount > 0 && perUnit) return { sizeCr: Math.round(amount * perUnit * 100) / 100, sizeText: null };
  // "[●] (count not disclosed in draft)" is a placeholder, not a count
  const shares = [structure.total_issue_size_shares, structure.fresh_issue_shares].find(
    (value) => typeof value === 'string' && /\d/.test(value) && !value.includes('●')
  );
  return { sizeCr: null, sizeText: shares ? `${shares.trim()} shares` : null };
};

// Registrars (RTAs) the extractor sometimes lists alongside the lead managers
const REGISTRAR_PATTERN = /kfin|link intime|mufg intime|bigshare|skyline financial|cameo corporate|purva sharegistry|maashitla|integrated registry/i;
// Cover-page labels the extractor keeps in front of a name ("MAIL SBI Capital
// Markets Limited", "BOOK RUNNING LEAD MANAGERS TO THE OFFER Systematix ...")
// The connector the label leaves behind goes too ("... LEAD MANAGER OF Choice Capital")
const LABEL_PREFIX =
  /^.*\b(?:telephone|e-?mail|mail|website|contact person|logo|inves\w*\s+banking|lead\s+manager\w*|to\s+the\s+(?:offer|issue))\b[\s:,-]*(?:\b(?:of|to|for|the|and)\b\s+)*/i;
const COMPANY_SUFFIX = /\b(?:limited|ltd\.?|llp)$/i;

// The connector left when the backend already removed a label ("OF Choice
// Capital Advisors Private Limited"). "the" is excluded: a few real names open
// with it, and none open with these.
const LEADING_CONNECTOR = /^(?:\b(?:of|to|for|and)\b\s+)+/i;

// "NAME AND LOGO OF THE REGISTRAR ... TELEPHONE KFin Technologies Limited" -> "KFin Technologies Limited"
export const stripExtractionLabels = (name) =>
  typeof name === 'string' ? name.replace(LABEL_PREFIX, '').replace(LEADING_CONNECTOR, '').replace(/\s+/g, ' ').trim() : '';

// Book running lead managers named in the DRHP, lead left first
export const leadManagersFromDetail = (detail) => {
  const names = [detail?.leadManager, ...(detail?.leadManagerObj?.allLeadManagers || [])]
    .filter((name) => typeof name === 'string')
    .map(stripExtractionLabels)
    // "Axis Capital Limited and IIFL Capital Services Limited" -> two managers
    .flatMap((name) => name.split(/(?<=\b(?:limited|ltd\.?|llp))\s+and\s+/i))
    .map((name) => name.trim())
    .filter((name) => name.length > 3 && COMPANY_SUFFIX.test(name) && !REGISTRAR_PATTERN.test(name));
  const seen = new Set();
  return names.filter((name) => {
    const key = name.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// The filing page, which reads the same tracker record the row came from
const detailPathFor = (id) => `/drhp-filings/${id}`;

const IST_DATE = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' });

// '2026-09-21T20:15:58+00:00' -> '2026-09-22' (the IST calendar date)
export const istDate = (timestamp) => {
  const date = new Date(timestamp || '');
  return timestamp && !Number.isNaN(date.getTime()) ? IST_DATE.format(date) : null;
};

// 'Sterlite Electric Limited – Second Addendum to DRHP' -> { company, note }
export const splitFilingName = (name) => {
  const text = String(name || '').replace(/\s+/g, ' ').trim();
  const match = text.match(/\s+[–—-]\s+(.*\b(?:addendum|corrigendum)\b.*)$/i);
  return match ? { company: text.slice(0, match.index).trim(), note: match[1].trim() } : { company: text, note: null };
};

// Of a company's tracker entries: the approved (RHP) one, then an approved analysis, then the newest
const leadFirst = (a, b) =>
  Number(b.status === 'approved') - Number(a.status === 'approved') ||
  Number(b.pipeline_status === 'approved') - Number(a.pipeline_status === 'approved') ||
  (b.drhp_filed_on || '').localeCompare(a.drhp_filed_on || '');

/**
 * @param {object} data
 * @param {object[]} data.records - GET /api/core/ipos/drhp-tracker items
 * @param {object} data.detailsById - mapIpoDetailResponse output keyed by id
 * @param {object[]} data.gmpRows - GMP board rows (selectGmpBoardRows)
 * @param {object[]} data.scheduleRows - NSE schedule rows (selectOpeningSoonIpos + selectSmeOpeningSoonIpos)
 */
export const buildDrhpRows = ({ records = [], detailsById = {}, gmpRows = [], scheduleRows = [] }) => {
  // The tracker can list a company twice (e.g. a DRHP entry plus one created from the exchange schedule)
  const byCompany = new Map();
  records
    .filter((record) => record?.company_name)
    .forEach((record) => {
      const key = matchKey(splitFilingName(record.company_name).company);
      byCompany.set(key, [...(byCompany.get(key) || []), record]);
    });

  return [...byCompany.values()]
    .map((group) => {
      const [record] = [...group].sort(leadFirst);
      const { company } = splitFilingName(record.company_name);
      const note = group.map((entry) => splitFilingName(entry.company_name).note).find(Boolean) || null;
      const detail = detailsById[record.id] || null;
      const schedule = scheduleRows.find((row) => isSameCompany(row.company, company)) || null;
      const gmp = gmpRows.find((row) => isSameCompany(row.name, company)) || null;

      let board = ['mainboard', 'sme'].includes(record.board) ? record.board : null;
      if (!board) board = schedule?.board || (gmp ? (gmp.board === 'SME' ? 'sme' : 'mainboard') : null);
      if (!board && detail?.exchanges) board = deriveBoard(detail.type, detail.exchanges);

      const exchanges = exchangeList(firstText(gmp?.exchange, record.listing_exchange, detail?.exchanges, schedule ? 'NSE' : null), board);
      // Exchange schedules know when bidding starts; before that the tracker's own status applies
      const status = statusFromMarket(schedule, gmp) || TRACKER_STATUS[record.status] || 'drhp_filed';
      const docType = docTypeFor(status);
      const fromDetail = issueFromDetail(detail);
      const sizeCr = schedule?.sizeCr ?? gmp?.sizeCr ?? fromDetail.sizeCr ?? null;
      const leadManagers = leadManagersFromDetail(detail);
      const drhpFiledOn = group.map((entry) => istDate(entry.drhp_filed_on)).filter(Boolean).sort()[0] || null;
      const rhpFiledOn = group.map((entry) => istDate(entry.rhp_filed_on)).filter(Boolean).sort().pop() || null;

      return {
        id: String(record.id),
        company,
        note,
        sector: firstText(detail?.productCategories?.[0]) || null,
        cin: firstText(...group.map((entry) => entry.cin), detail?.cin) || '',
        board,
        exchanges,
        status,
        trackerStatus: record.status || null,
        docType,
        openOn: schedule?.openOn || gmp?.openOn || null,
        closeOn: schedule?.closeOn || gmp?.closeOn || null,
        priceBand: firstText(schedule?.priceBand, gmp?.price ? `₹${gmp.price}` : null, detail?.priceBand) || null,
        sizeCr,
        sizeText: sizeCr ? null : fromDetail.sizeText,
        leadManager: leadManagers[0] || '',
        leadManagers,
        drhpFiledOn,
        rhpFiledOn,
        // Date of the document the row shows; an issue past the DRHP stage with
        // no RHP date in the tracker falls back to its DRHP date, labelled as such
        documentDate: docType === 'DRHP' ? drhpFiledOn : rhpFiledOn || drhpFiledOn,
        documentDateLabel: docType === 'DRHP' || rhpFiledOn ? 'Filed' : 'DRHP filed',
        addedAt: group.map((entry) => entry.drhp_filed_on).filter(Boolean).sort()[0] || null,
        logoUrl: detail?.logoUrl || null,
        pipelineStatus: record.pipeline_status || null,
        // The backend publishes a DRHP analysis once its extraction is approved
        analysisReady: record.pipeline_status === 'approved',
        detailPath: detailPathFor(record.id),
      };
    })
    .sort(compareDrhpRows);
};

// Live issues first, then by the latest date we know for each company
const activityDate = (row) => row.openOn || row.documentDate || (row.addedAt || '').slice(0, 10);

export const compareDrhpRows = (a, b) =>
  DRHP_STATUS[a.status].rank - DRHP_STATUS[b.status].rank ||
  activityDate(b).localeCompare(activityDate(a)) ||
  a.company.localeCompare(b.company);

export const filterDrhpRows = (rows, { tab = 'all', status = 'all', exchange = 'all', query = '' } = {}) => {
  const needle = query.trim().toLowerCase();
  return rows
    .filter(drhpTabByKey(tab).test)
    .filter((row) => status === 'all' || row.status === status)
    .filter((row) => exchange === 'all' || row.exchanges.includes(exchange))
    .filter(
      (row) =>
        !needle ||
        [row.company, row.note, row.cin, row.sector, ...(row.leadManagers || [row.leadManager])]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(needle)
    );
};

const HOURS_48 = 48 * 3600 * 1000;
const PRE_RHP = ['under_review', 'drhp_filed'];

export const computeDrhpStats = (rows, now = new Date()) => {
  const monthKey = todayIso(now).slice(0, 7);
  return {
    total: rows.length,
    addedThisMonth: rows.filter((row) => (row.drhpFiledOn || '').slice(0, 7) === monthKey).length,
    underReview: rows.filter((row) => row.status === 'under_review').length,
    // RHP filed, with bidding scheduled, open or done
    approved: rows.filter((row) => !PRE_RHP.includes(row.status)).length,
    biddingOpen: rows.filter((row) => row.status === 'bidding_open').length,
    analysed: rows.filter((row) => row.analysisReady).length,
    recent: rows.filter((row) => row.addedAt && now - new Date(row.addedAt) <= HOURS_48).length,
  };
};

// Book running lead managers across the extracted DRHPs, most mandates first
export const topMerchantBankers = (rows, limit = 5) => {
  const counts = new Map();
  rows.forEach((row) => {
    (row.leadManagers || [row.leadManager]).filter(Boolean).forEach((name) => counts.set(name, (counts.get(name) || 0) + 1));
  });
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, limit)
    .map(([name, count]) => ({ name, count }));
};

const csvCell = (value) => {
  if (value === null || value === undefined) return '';
  const text = String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const buildDrhpCsv = (rows) => {
  const header = [
    'Company', 'Note', 'CIN', 'Board', 'Exchanges', 'Document', 'Status', 'DRHP Filed', 'RHP Filed', 'Opening Date',
    'Closing Date', 'Price Band', 'Issue Size (INR Cr)', 'Issue Size (other)', 'Lead Managers', 'Analysis Ready',
  ];
  const lines = rows.map((row) =>
    [
      row.company,
      row.note,
      row.cin,
      row.board === 'sme' ? 'SME' : row.board === 'mainboard' ? 'Mainboard' : '',
      row.exchanges.join(' / '),
      row.docType,
      DRHP_STATUS[row.status].label,
      row.drhpFiledOn,
      row.rhpFiledOn,
      row.openOn,
      row.closeOn,
      row.priceBand,
      row.sizeCr,
      row.sizeText,
      (row.leadManagers || [row.leadManager]).filter(Boolean).join(' / '),
      row.analysisReady ? 'Yes' : 'No',
    ]
      .map(csvCell)
      .join(',')
  );
  return [header.map(csvCell).join(','), ...lines].join('\r\n');
};
